import { Button, SkeletonTable, SkeletonText } from '@dev-spendesk/grapes';
import escapeRegExp from 'lodash/escapeRegExp';
import React, { useEffect, useMemo, useState } from 'react';

import { SearchFilter } from 'common/components/SearchFilter';
import { useTranslation } from 'common/hooks/useTranslation';
import { useFeature } from 'src/core/common/hooks/useFeature';
import FEATURES from 'src/core/constants/features';

import { TeamsTab, type TeamsTabProps, type TeamModal } from './TeamsTab';
import { ApprovalByDimensionsActivationBanner } from '../../../approval-by-dimensions/ApprovalByDimensionsActivationBanner';

export type TeamsTabContainerProps = {
  isLoadingUsers: boolean;
  fetchApprovalSchemes: () => void;
} & TeamsTabProps;

export const TeamsTabContainer = ({
  users,
  groups,
  approvalSchemes,
  isLoadingUsers,
  // actions
  fetchApprovalSchemes,
  ...rest
}: TeamsTabContainerProps) => {
  const { t } = useTranslation('global');

  const [openModal, setOpenModal] = useState<TeamModal | null>(null);
  const [searchPattern, setSearchPattern] = useState('');

  const filteredGroups = useMemo(() => {
    if (!searchPattern) {
      return groups;
    }
    const escapedSearchPattern = escapeRegExp(searchPattern);
    return groups.filter((group) =>
      group.name?.match(new RegExp(escapedSearchPattern, 'i')),
    );
  }, [groups, searchPattern]);

  useEffect(() => {
    fetchApprovalSchemes();
  }, []);

  const isMultidimensionalApprovalWorkflowEnabled = useFeature(
    FEATURES.MULTIDIMENSIONAL_APPROVAL_WORKFLOW,
  );

  return (
    <div className="w-full max-w-lg self-center">
      <div className="mb-16 flex items-center">
        <div className="flex flex-1 flex-col">
          <div className="text-content-primary heading-l">
            {t('members.teamsTitle')}
          </div>
          <span className="text-content-secondary-bg-primary body-m mt-8">
            <span>{t('members.teamsCount', { count: groups?.length })}</span>
          </span>
        </div>
        <SearchFilter
          className="mr-16"
          search={searchPattern}
          setSearch={(search) => setSearchPattern(search)}
          minLength={0}
          placeholder={t('members.searchInTeams')}
        />
        <Button
          isDisabled={isLoadingUsers || !groups || !users?.length}
          onClick={() => setOpenModal('createTeam')}
          variant="primaryBrand"
          text={t('members.createANewTeam')}
        />
      </div>

      {!isMultidimensionalApprovalWorkflowEnabled && (
        <ApprovalByDimensionsActivationBanner className="mb-16" />
      )}

      {!isLoadingUsers && groups && users && users?.length > 0 ? (
        <TeamsTab
          groups={filteredGroups}
          users={users}
          approvalSchemes={approvalSchemes}
          openModal={openModal}
          setOpenModal={setOpenModal}
          {...rest}
        />
      ) : (
        <SkeletonTable
          numberOfRows={3}
          withHeader
          columns={[
            {
              header: <SkeletonText width="50%" />,
              cell: <SkeletonText width="70%" />,
              width: '25%',
            },
            {
              header: <SkeletonText width="20%" />,
              cell: <SkeletonText width="30%" />,
              width: '75%',
            },
          ]}
        />
      )}
    </div>
  );
};
